import { useEffect, useRef, useState, type ReactNode } from "react";

interface DeferredMountProps {
  children: ReactNode;
  /** Start loading this far before the sentinel enters the viewport */
  rootMargin?: string;
  /**
   * Absolute fallback: mount after this many ms even if never near viewport.
   * Uses setTimeout (not requestIdleCallback) so Lighthouse idle doesn't
   * eagerly pull heavy below-fold chunks into the lab window.
   */
  idleTimeoutMs?: number;
  /** Earliest time intersection/idle may mount (keeps work off first paint) */
  minDelayMs?: number;
}

/**
 * Defers mounting children until near the viewport (after minDelay) or a
 * fallback timeout. Keeps below-fold JS off the critical path.
 */
const DeferredMount = ({
  children,
  rootMargin = "600px",
  idleTimeoutMs = 8000,
  minDelayMs = 0,
}: DeferredMountProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let cancelled = false;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;
    let minDelayId: ReturnType<typeof setTimeout> | undefined;
    let observer: IntersectionObserver | undefined;
    let intersected = false;
    let minDelayPassed = minDelayMs <= 0;

    const tryEnable = () => {
      if (cancelled) return;
      if (minDelayPassed && intersected) setReady(true);
    };

    const enableFallback = () => {
      if (!cancelled) setReady(true);
    };

    if (minDelayMs > 0) {
      minDelayId = setTimeout(() => {
        minDelayPassed = true;
        tryEnable();
      }, minDelayMs);
    }

    const el = sentinelRef.current;
    if (el && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            intersected = true;
            observer?.disconnect();
            tryEnable();
            // If still waiting on minDelay, mark for when it passes
          }
        },
        { rootMargin },
      );
      observer.observe(el);
    } else {
      // No IO support — rely on fallback timeout
      intersected = true;
    }

    fallbackId = setTimeout(enableFallback, idleTimeoutMs);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (fallbackId !== undefined) clearTimeout(fallbackId);
      if (minDelayId !== undefined) clearTimeout(minDelayId);
    };
  }, [ready, rootMargin, idleTimeoutMs, minDelayMs]);

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-0 w-0 overflow-hidden" />
      {ready ? children : null}
    </>
  );
};

export default DeferredMount;
